import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { ArrowLeft, ArrowRight, CheckCircle, Home, Layers, MapPin, DollarSign, Users, Briefcase, Clock, Sparkles } from 'lucide-react';
import { CriteriaSelector } from '@/components/CriteriaSelector';
import { LocationSelector } from '@/components/LocationSelector';
import { API } from '@/config';
import axios from 'axios';
import { toast } from 'sonner';

const CreateSearchForm = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    property_type: '',
    usage_type: 'Résidence principale',
    min_surface: '',
    max_surface: '',
    rooms: '',
    location_data: {
      address: '',
      coords: [43.5808, 7.1251],
      radius: 500
    },
    location: '',
    preferred_areas: [],
    min_budget: '',
    max_budget: '',
    financing_type: '',
    criteria: {},
    is_urgent: false,
    visit_possible: '',
    decision_time: '',
    signature_time: '',
    annual_income: '',
    personal_apport: '',
    employment_status: '',
    family_composition: '',
    project_description: ''
  });

  const [newArea, setNewArea] = useState('');
  const [generatingText, setGeneratingText] = useState(false);

  // Générer automatiquement le texte projet quand on arrive à l'étape 3
  useEffect(() => {
    if (step === 3 && !formData.project_description && formData.first_name) {
      generateProjectText();
    }
  }, [step]);

  const generateProjectText = async () => {
    setGeneratingText(true);
    try {
      const response = await axios.post(`${API}/generate-project-description`, {
        first_name: formData.first_name,
        property_type: formData.property_type,
        rooms: formData.rooms,
        min_surface: formData.min_surface,
        max_surface: formData.max_surface,
        location: formData.location,
        address: formData.location_data?.address,
        max_budget: formData.max_budget,
        family_composition: formData.family_composition,
        criteria: formData.criteria
      });
      
      if (response.data.description) {
        setFormData(prev => ({ ...prev, project_description: response.data.description }));
      }
    } catch (error) {
      console.error('Error generating text:', error);
    } finally {
      setGeneratingText(false);
    }
  };

  const addArea = () => {
    if (newArea.trim()) {
      setFormData(prev => ({
        ...prev,
        preferred_areas: [...prev.preferred_areas, newArea.trim()]
      }));
      setNewArea('');
    }
  };

  const removeArea = (index) => {
    setFormData(prev => ({
      ...prev,
      preferred_areas: prev.preferred_areas.filter((_, i) => i !== index)
    }));
  };

  const handleSubmit = async () => {
    setLoading(true);
    try {
      const title = `${formData.first_name} ${formData.last_name.substring(0, 2).toUpperCase()}***`;
      
      const payload = {
        first_name: formData.first_name,
        last_name: formData.last_name,
        title: title,
        property_type: formData.property_type,
        usage_type: formData.usage_type,
        min_surface: formData.min_surface ? parseFloat(formData.min_surface) : null,
        max_surface: formData.max_surface ? parseFloat(formData.max_surface) : null,
        rooms: formData.rooms ? parseInt(formData.rooms) : null,
        location: formData.location,
        location_data: formData.location_data,
        preferred_areas: formData.preferred_areas,
        min_budget: formData.min_budget ? parseFloat(formData.min_budget) : null,
        max_budget: parseFloat(formData.max_budget),
        financing_type: formData.financing_type || null,
        active_since: '1 semaine',
        visit_possible: formData.visit_possible || null,
        decision_time: formData.decision_time || null,
        signature_time: formData.signature_time || null,
        annual_income: formData.annual_income ? parseFloat(formData.annual_income) : null,
        personal_apport: formData.personal_apport ? parseFloat(formData.personal_apport) : null,
        employment_status: formData.employment_status || null,
        family_composition: formData.family_composition || null,
        project_description: formData.project_description,
        criteria: formData.criteria,
        is_urgent: formData.is_urgent
      };

      await axios.post(`${API}/acquirer-ads`, payload, {
        withCredentials: true
      });

      toast.success('Annonce créée avec succès !');
      navigate('/');
    } catch (error) {
      console.error('Error:', error);
      toast.error(error.response?.data?.detail || 'Erreur lors de la création');
    } finally {
      setLoading(false);
    }
  };

  const canProceed = () => {
    if (step === 1) return formData.first_name && formData.last_name && formData.property_type && formData.location;
    if (step === 2) return formData.max_budget;
    if (step === 3) return formData.project_description;
    return true;
  };

  return (
    <div className="min-h-screen bg-background">
      <header className="bg-white border-b border-border py-4 px-6 mb-8">
        <div className="max-w-4xl mx-auto flex items-center gap-4">
          <Button
            onClick={() => navigate('/')}
            variant="ghost"
            size="icon"
          >
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <h1 className="font-display text-2xl font-bold text-foreground">
            Créer mon annonce
          </h1>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-2 md:px-4 pb-12">
        <div className="mb-8">
          <div className="flex items-center justify-between mb-4">
            {[1, 2, 3, 4].map((s) => (
              <div key={s} className="flex items-center flex-1">
                <div className={`w-10 h-10 rounded-full flex items-center justify-center font-bold ${
                  s === step 
                    ? 'bg-blue-600 text-white' 
                    : s < step
                    ? 'bg-green-600 text-white'
                    : 'bg-gray-300 text-gray-600'
                }`}>
                  {s < step ? <CheckCircle className="w-5 h-5" /> : s}
                </div>
                {s < 4 && (
                  <div className={`flex-1 h-1 mx-2 ${
                    s < step ? 'bg-green-600' : 'bg-gray-300'
                  }`} />
                )}
              </div>
            ))}
          </div>
          <div className="flex justify-between text-xs md:text-sm text-gray-700">
            <span className={step === 1 ? 'font-bold' : ''}>Identité & Bien</span>
            <span className={step === 2 ? 'font-bold' : ''}>Budget & Critères</span>
            <span className={step === 3 ? 'font-bold' : ''}>Profil</span>
            <span className={step === 4 ? 'font-bold' : ''}>Planning</span>
          </div>
        </div>

        <div className="bg-white/95 backdrop-blur-sm rounded-3xl p-6 md:p-8 shadow-xl">
          {step === 1 && (
            <div className="space-y-6">
              <h3 className="text-xl font-bold text-blue-600">Étape 1 : Identité et Type de bien</h3>
              
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <Label>Prénom *</Label>
                  <Input
                    value={formData.first_name}
                    onChange={(e) => setFormData(prev => ({...prev, first_name: e.target.value}))}
                    placeholder="Jean"
                  />
                </div>
                <div>
                  <Label>Nom *</Label>
                  <Input
                    value={formData.last_name}
                    onChange={(e) => setFormData(prev => ({...prev, last_name: e.target.value}))}
                    placeholder="DUPONT"
                  />
                </div>
              </div>

              <div>
                <Label>Type de bien *</Label>
                <select
                  value={formData.property_type}
                  onChange={(e) => setFormData(prev => ({...prev, property_type: e.target.value}))}
                  className="w-full rounded-lg border border-input bg-background px-3 py-2 text-sm"
                >
                  <option value="">Sélectionnez</option>
                  <option value="Appartement">Appartement</option>
                  <option value="Maison">Maison</option>
                </select>
              </div>

              <div>
                <Label>Usage</Label>
                <select
                  value={formData.usage_type}
                  onChange={(e) => setFormData(prev => ({...prev, usage_type: e.target.value}))}
                  className="w-full rounded-lg border border-input bg-background px-3 py-2 text-sm"
                >
                  <option value="Résidence principale">Résidence principale</option>
                  <option value="Résidence secondaire">Résidence secondaire</option>
                  <option value="Investissement locatif">Investissement locatif</option>
                  <option value="Construction">Construction</option>
                </select>
              </div>

              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <Label>Surface min (m²)</Label>
                  <Input
                    type="number"
                    value={formData.min_surface}
                    onChange={(e) => setFormData(prev => ({...prev, min_surface: e.target.value}))}
                  />
                </div>
                <div>
                  <Label>Surface max (m²)</Label>
                  <Input
                    type="number"
                    value={formData.max_surface}
                    onChange={(e) => setFormData(prev => ({...prev, max_surface: e.target.value}))}
                  />
                </div>
              </div>

              <div>
                <Label>Nombre de pièces</Label>
                <Input
                  type="number"
                  value={formData.rooms}
                  onChange={(e) => setFormData(prev => ({...prev, rooms: e.target.value}))}
                />
              </div>

              <div>
                <Label>Localisation avec carte *</Label>
                <LocationSelector
                  location={formData.location_data}
                  onChange={(locationData) => {
                    const addressParts = locationData.address.split(',');
                    const city = addressParts[addressParts.length - 1]?.trim() || locationData.address || 'Antibes';
                    setFormData(prev => ({
                      ...prev,
                      location_data: locationData,
                      location: city
                    }));
                  }}
                />
              </div>
            </div>
          )}

          {step === 2 && (
            <div className="space-y-6">
              <h3 className="text-xl font-bold text-blue-600">Étape 2 : Budget et Critères</h3>

              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <Label>Budget minimum (€)</Label>
                  <Input
                    type="number"
                    value={formData.min_budget}
                    onChange={(e) => setFormData(prev => ({...prev, min_budget: e.target.value}))}
                  />
                </div>
                <div>
                  <Label>Budget maximum (€) *</Label>
                  <Input
                    type="number"
                    value={formData.max_budget}
                    onChange={(e) => setFormData(prev => ({...prev, max_budget: e.target.value}))}
                  />
                </div>
              </div>

              <div>
                <Label>Type de financement</Label>
                <select
                  value={formData.financing_type}
                  onChange={(e) => setFormData(prev => ({...prev, financing_type: e.target.value}))}
                  className="w-full rounded-lg border border-input bg-background px-3 py-2 text-sm"
                >
                  <option value="">Sélectionnez</option>
                  <option value="Préapprouvé Crédit Agricole">Préapprouvé Crédit Agricole</option>
                  <option value="Prêt bancaire validé">Prêt bancaire validé</option>
                  <option value="Prêt bancaire en cours">Prêt bancaire en cours</option>
                  <option value="Achat comptant">Achat comptant</option>
                  <option value="Financement mixte">Financement mixte</option>
                </select>
              </div>

              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="urgent"
                  checked={formData.is_urgent}
                  onChange={(e) => setFormData(prev => ({...prev, is_urgent: e.target.checked}))}
                  className="w-4 h-4 rounded"
                />
                <Label htmlFor="urgent" className="cursor-pointer">
                  🔥 Ma recherche est URGENTE
                </Label>
              </div>

              <div>
                <h4 className="font-bold text-gray-800 mb-3">🎯 Critères de recherche</h4>
                <p className="text-sm text-gray-600 mb-4">
                  Cochez tous les critères qui vous intéressent
                </p>
                <CriteriaSelector
                  selectedCriteria={formData.criteria}
                  onChange={(criteria) => setFormData(prev => ({...prev, criteria}))}
                  propertyType={formData.property_type}
                />
              </div>
            </div>
          )}

          {step === 3 && (
            <div className="space-y-6">
              <h3 className="text-xl font-bold text-blue-600">Étape 3 : Profil</h3>

              <div>
                <Label>Composition familiale</Label>
                <Input
                  value={formData.family_composition}
                  onChange={(e) => setFormData(prev => ({...prev, family_composition: e.target.value}))}
                  placeholder="Ex: Couple, 2 enfants"
                />
              </div>

              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <Label>Revenus annuels (€)</Label>
                  <Input
                    type="number"
                    value={formData.annual_income}
                    onChange={(e) => setFormData(prev => ({...prev, annual_income: e.target.value}))}
                  />
                </div>
                <div>
                  <Label>Apport personnel (€)</Label>
                  <Input
                    type="number"
                    value={formData.personal_apport}
                    onChange={(e) => setFormData(prev => ({...prev, personal_apport: e.target.value}))}
                  />
                </div>
              </div>

              <div>
                <Label>Statut professionnel</Label>
                <Input
                  value={formData.employment_status}
                  onChange={(e) => setFormData(prev => ({...prev, employment_status: e.target.value}))}
                  placeholder="Ex: CDI stable, Profession libérale"
                />
              </div>

              <div>
                <div className="flex items-center justify-between mb-2">
                  <Label>Description du projet *</Label>
                  {!generatingText && (
                    <Button
                      type="button"
                      onClick={generateProjectText}
                      size="sm"
                      variant="outline"
                      className="text-xs"
                    >
                      <Sparkles className="w-3 h-3 mr-1" />
                      Générer avec IA
                    </Button>
                  )}
                </div>
                <Textarea
                  rows={6}
                  value={formData.project_description}
                  onChange={(e) => setFormData(prev => ({...prev, project_description: e.target.value}))}
                  placeholder={generatingText ? "Génération en cours..." : "Décrivez votre projet ou cliquez 'Générer avec IA'"}
                  disabled={generatingText}
                />
                {generatingText && (
                  <p className="text-xs text-muted-foreground mt-2 flex items-center gap-2">
                    <Sparkles className="w-3 h-3 animate-pulse" />
                    Génération du texte personnalisé...
                  </p>
                )}
              </div>
            </div>
          )}

          {step === 4 && (
            <div className="space-y-6">
              <h3 className="text-xl font-bold text-blue-600">Étape 4 : Planning</h3>

              <div>
                <Label>Visite possible</Label>
                <Input
                  value={formData.visit_possible}
                  onChange={(e) => setFormData(prev => ({...prev, visit_possible: e.target.value}))}
                  placeholder="Ex: 48h, Week-ends uniquement"
                />
              </div>

              <div>
                <Label>Décision sous</Label>
                <Input
                  value={formData.decision_time}
                  onChange={(e) => setFormData(prev => ({...prev, decision_time: e.target.value}))}
                  placeholder="Ex: 1 semaine"
                />
              </div>

              <div>
                <Label>Signature dans</Label>
                <Input
                  value={formData.signature_time}
                  onChange={(e) => setFormData(prev => ({...prev, signature_time: e.target.value}))}
                  placeholder="Ex: 3 semaines"
                />
              </div>
            </div>
          )}

          <div className="flex items-center justify-between mt-8 pt-8 border-t">
            <Button
              variant="outline"
              onClick={() => setStep(step - 1)}
              disabled={step === 1}
              className="rounded-full"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Précédent
            </Button>

            {step < 4 ? (
              <Button
                onClick={() => setStep(step + 1)}
                disabled={!canProceed()}
                className="rounded-full bg-blue-600 hover:bg-blue-700 font-semibold"
              >
                Suivant
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            ) : (
              <Button
                onClick={handleSubmit}
                disabled={!canProceed() || loading}
                className="rounded-full bg-blue-600 hover:bg-blue-700 font-semibold"
              >
                {loading ? 'Création...' : 'Publier mon annonce'}
                <CheckCircle className="w-4 h-4 ml-2" />
              </Button>
            )}
          </div>
        </div>
      </main>
    </div>
  );
};

export default CreateSearchForm;
